import React, { useEffect, useState } from 'react';
import { Bell, MapPin, Search, Edit3, Trash2, ToggleLeft, ToggleRight, X } from 'lucide-react';
import { ACTIVITY_LABEL, ActivityType } from '../lib/activityTypes';
import { useActivityCatalog, ActivityDefinition } from '../lib/activityCatalog';
import { handleApiError } from '../lib/api';

export type SavedSearch = {
  id: string;
  name: string;
  query: string;
  type: 'all' | 'wells' | 'users' | 'activities' | 'files';
  filters?: Record<string, any>;
  isActive?: boolean;
  createdAt: string;
};

export type SearchForm = {
  searchName: string;
  activityDefinitionId: string;
  activityType: ActivityType;
  location: string;
  company: string;
  well: string;
  channel: string;
};

type ApiCallOptions = Omit<RequestInit, 'body'> & { body?: any };

interface Props {
  isOpen: boolean;
  onClose: () => void;
  savedSearches: SavedSearch[];
  loadSavedSearches: () => Promise<void>;
  apiCall: (endpoint: string, options?: ApiCallOptions) => Promise<any>;
  user: { role: 'USER' | 'ADMIN'; emailVerifiedAt?: string | null } | null;
  setToast: (toast: { message: string; type?: 'success' | 'error' } | null) => void;
  toggleSearchStatus: (id: string, isActive?: boolean) => Promise<void>;
  initialSearchForm?: Partial<SearchForm> | null;
}

const ActiveSearchModal: React.FC<Props> = ({
  isOpen,
  onClose,
  savedSearches,
  loadSavedSearches,
  apiCall,
  user,
  setToast,
  toggleSearchStatus,
  initialSearchForm,
}) => {
  const [searchModalTab, setSearchModalTab] = useState<'create' | 'manage'>('create');
  const [editingSearch, setEditingSearch] = useState<SavedSearch | null>(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [searchForm, setSearchForm] = useState<SearchForm>({
    searchName: '',
    activityDefinitionId: '',
    activityType: 'DRILLING',
    location: '',
    company: '',
    well: '',
    channel: 'For you',
  });

  const { catalog } = useActivityCatalog();
  const groupedActivities = (catalog || []).reduce<Record<ActivityType, ActivityDefinition[]>>(
    (acc, def) => {
      (acc[def.type] ||= []).push(def);
      return acc;
    },
    {} as Record<ActivityType, ActivityDefinition[]>
  );

  useEffect(() => {
    if (catalog && catalog.length && !searchForm.activityDefinitionId) {
      const first =
        catalog.find(d => d.type === searchForm.activityType) || catalog[0];
      setSearchForm(prev => ({
        ...prev,
        activityDefinitionId: first.id,
        activityType: first.type,
      }));
    }
  }, [catalog, searchForm.activityDefinitionId, searchForm.activityType]);

  useEffect(() => {
    if (isOpen) {
      setSearchForm(prev => ({
        ...prev,
        ...(initialSearchForm || {}),
      }));
    }
  }, [isOpen, initialSearchForm]);

  const resetSearchForm = () => {
    setSearchForm({
      searchName: '',
      activityDefinitionId: '',
      activityType: 'DRILLING',
      location: '',
      company: '',
      well: '',
      channel: 'For you',
    });
    setEditingSearch(null);
  };

  const handleSearchInputChange = (field: keyof SearchForm, value: string) => {
    setSearchForm(prev => ({ ...prev, [field]: value }));
  };

  const fillCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(pos => {
        handleSearchInputChange(
          'location',
          `${pos.coords.latitude.toFixed(6)}, ${pos.coords.longitude.toFixed(6)}`
        );
      });
    }
  };

  const handleActivityChange = (id: string) => {
    const def = catalog?.find(d => d.id === id);
    setSearchForm(prev => ({
      ...prev,
      activityDefinitionId: id,
      activityType: def ? def.type : prev.activityType,
    }));
  };

  const handleSearchSubmit = async () => {
    if (!searchForm.searchName || !searchForm.activityDefinitionId) return;
    if (user && user.role !== 'ADMIN' && !user.emailVerifiedAt) {
      setToast({ message: 'Please verify your email to create a search.', type: 'error' });
      return;
    }
    try {
      if (editingSearch) {
        await apiCall(`/searches/saved/${editingSearch.id}`, {
          method: 'PUT',
          body: {
            name: searchForm.searchName,
            query: searchForm.activityDefinitionId,
            type: 'all',
            filters: {
              activityDefinitionId: searchForm.activityDefinitionId,
              activityType: searchForm.activityType,
              location: searchForm.location,
              company: searchForm.company,
              well: searchForm.well,
              channel: searchForm.channel,
            },
          },
        });
        setToast({ message: 'Search updated successfully', type: 'success' });
      } else {
        await apiCall('/searches/saved', {
          method: 'POST',
          body: {
            name: searchForm.searchName,
            query: searchForm.activityDefinitionId,
            type: 'all',
            filters: {
              activityDefinitionId: searchForm.activityDefinitionId,
              activityType: searchForm.activityType,
              location: searchForm.location,
              company: searchForm.company,
              well: searchForm.well,
              channel: searchForm.channel,
            },
            isActive: true,
          },
        });
        setToast({ message: 'Search created successfully', type: 'success' });
      }
      await loadSavedSearches();
      resetSearchForm();
      onClose();
    } catch (e: any) {
      setToast({ message: handleApiError(e), type: 'error' });
    }
  };

  const handleEditSearch = (search: SavedSearch) => {
    setEditingSearch(search);
    setSearchForm({
      searchName: search.name,
      activityDefinitionId: search.filters?.activityDefinitionId || '',
      activityType: (search.filters?.activityType as ActivityType) || 'DRILLING',
      location: search.filters?.location || '',
      company: search.filters?.company || '',
      well: search.filters?.well || '',
      channel: search.filters?.channel || 'For you',
    });
    setSearchModalTab('create');
  };

  const deleteSavedSearch = async (id: string) => {
    try {
      await apiCall(`/searches/saved/${id}`, { method: 'DELETE' });
      setToast({ message: 'Search deleted successfully', type: 'success' });
      await loadSavedSearches();
    } catch (e: any) {
      setToast({ message: handleApiError(e), type: 'error' });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => {
          onClose();
          resetSearchForm();
        }}
      ></div>
      <div className="relative bg-white dark:bg-black border border-gray-200 dark:border-gray-700 rounded-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {editingSearch ? 'Edit Search' : 'Active Search'}
          </h2>
          <button
            onClick={() => {
              onClose();
              resetSearchForm();
            }}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

          <div className="flex border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setSearchModalTab('create')}
              className={`flex-1 py-4 px-6 text-sm font-medium relative transition-colors ${
                searchModalTab === 'create'
                  ? 'text-gray-900 dark:text-white'
                  : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {editingSearch ? 'Edit Search' : 'Create New'}
              {searchModalTab === 'create' && (
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-blue-500 rounded-full"></div>
              )}
            </button>
            <button
              onClick={() => setSearchModalTab('manage')}
              className={`flex-1 py-4 px-6 text-sm font-medium relative transition-colors ${
                searchModalTab === 'manage'
                  ? 'text-gray-900 dark:text-white'
                  : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Manage Existing ({savedSearches.length})
              {searchModalTab === 'manage' && (
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-blue-500 rounded-full"></div>
              )}
            </button>
          </div>

          {searchModalTab === 'create' && (
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Search Name *</label>
                  <input
                    type="text"
                    value={searchForm.searchName}
                    onChange={e => handleSearchInputChange('searchName', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Show results in</label>
                  <select
                    value={searchForm.channel}
                    onChange={e => handleSearchInputChange('channel', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="For you">For you</option>
                    <option value="Saved">Saved</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Activity *</label>
                  <select
                    value={searchForm.activityDefinitionId}
                    onChange={e => handleActivityChange(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Object.entries(groupedActivities).map(([type, defs]) => (
                      <optgroup key={type} label={ACTIVITY_LABEL[type as ActivityType]}>
                        {defs.map(def => (
                          <option key={def.id} value={def.id}>
                            {def.name}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <input type="hidden" value={searchForm.activityType} hidden />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Location</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchForm.location}
                      onChange={e => handleSearchInputChange('location', e.target.value)}
                      className="w-full px-3 py-2 pr-10 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={fillCurrentLocation}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                    >
                      <MapPin className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Company</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchForm.company}
                      onChange={e => handleSearchInputChange('company', e.target.value)}
                      className="w-full px-3 py-2 pr-10 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                    >
                      <Search className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Well</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchForm.well}
                      onChange={e => handleSearchInputChange('well', e.target.value)}
                      className="w-full px-3 py-2 pr-10 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                    >
                      <Search className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <button
                  type="button"
                  onClick={() => setShowHowItWorks(!showHowItWorks)}
                  className="flex items-center space-x-2 text-blue-300 hover:text-blue-200 transition-colors text-sm font-medium"
                >
                  <Bell className="w-4 h-4" />
                  <span>How Active Search works</span>
                  <svg
                    className={`w-4 h-4 transition-transform ${showHowItWorks ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showHowItWorks && (
                  <div className="mt-3 bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                    <p className="text-blue-200 text-sm">
                      Our AI scans wellsite activity, permits, and competitive intelligence matching your criteria.
                      Critical updates automatically flow to your <strong>For You</strong> tab, keeping you informed without the manual work.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => { onClose(); resetSearchForm(); }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSearchSubmit}
                  disabled={!searchForm.searchName || !searchForm.activityDefinitionId}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingSearch ? 'Update Search' : 'Save Search'}
                </button>
              </div>
            </div>
          )}

          {searchModalTab === 'manage' && (
            <div className="p-6 min-h-[400px]">
              <div className="space-y-4">
                {savedSearches.length === 0 ? (
                  <div className="text-center py-8">
                    <Search className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400 mb-4">No saved searches yet</p>
                    <button
                      onClick={() => setSearchModalTab('create')}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
                    >
                      Create Your First Search
                    </button>
                  </div>
                ) : (
                  savedSearches.map(search => (
                    <div key={search.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-3">
                              <h4 className="font-medium text-gray-900 dark:text-white">{search.name}</h4>
                              <button
                                onClick={() => toggleSearchStatus(search.id, search.isActive)}
                                className={`p-1 rounded transition-colors ${
                                  search.isActive !== false ? 'text-green-600 hover:text-green-500 dark:text-green-400 dark:hover:text-green-300' : 'text-gray-500 hover:text-gray-400'
                                }`}
                                title={search.isActive !== false ? 'Disable search' : 'Enable search'}
                              >
                                {search.isActive !== false ? (
                                  <ToggleRight className="w-5 h-5" />
                                ) : (
                                  <ToggleLeft className="w-5 h-5" />
                                )}
                              </button>
                              <span
                                className={`text-xs px-2 py-1 rounded-full ${
                                  search.isActive !== false ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                }`}
                              >
                                {search.isActive !== false ? 'Active' : 'Disabled'}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleEditSearch(search)}
                                className="p-1 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                title="Edit search"
                              >
                                <Edit3 className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => deleteSavedSearch(search.id)}
                                className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                title="Delete search"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          <div className="text-gray-500 dark:text-gray-400 text-sm grid grid-cols-2 gap-x-4 gap-y-1">
                            <span>Type: {ACTIVITY_LABEL[(search.filters?.activityType as ActivityType) || search.query as ActivityType]}</span>
                            <span>Channel: {search.filters?.channel || 'For you'}</span>
                            {search.filters?.location && <span>Location: {search.filters.location}</span>}
                            {search.filters?.company && <span>Company: {search.filters.company}</span>}
                            {search.filters?.well && <span>Well: {search.filters.well}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
  );
};

export default ActiveSearchModal;
